import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';
import type { PluginListenerHandle } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';

/**
 * Google Play in-app flexible update flow.
 * 1. Checks for updates at startup and on resume.
 * 2. If available, starts a flexible (background) download via Play's native UI.
 * 3. When download completes, shows a persistent toast with "Restart" action.
 * 4. Falls back to opening Play Store if flexible update isn't available.
 * Only active on Android — no-op on web/iOS.
 */
export function useAppUpdate(): void {
  const { t } = useTranslation();
  const addToast = useToast();
  const hasStartedRef = useRef(false);
  const listenerRef = useRef<PluginListenerHandle | null>(null);

  const completeUpdate = useCallback(async () => {
    addToast(t('app_update.installing', 'Installerer oppdatering...'), 'info');
    try {
      await AppUpdate.completeFlexibleUpdate();
      // App restarts — code below won't execute
    } catch {
      AppUpdate.openAppStore().catch(() => {});
    }
  }, [addToast, t]);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;

    let mounted = true;

    const checkAndStartUpdate = async () => {
      if (hasStartedRef.current) return;

      try {
        const info = await AppUpdate.getAppUpdateInfo();
        if (!mounted) return;

        // A previous flexible download completed but wasn't installed yet
        if (info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          hasStartedRef.current = true;
          addToast(
            t('app_update.ready_to_install', 'Oppdatering lastet ned! Trykk for å installere.'),
            'info',
            { label: t('app_update.restart_button', 'Start på nytt'), onClick: completeUpdate },
          );
          return;
        }

        if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return;

        hasStartedRef.current = true;

        // Try flexible (background) update first
        if (info.flexibleUpdateAllowed) {
          // Listen BEFORE starting so we don't miss events
          listenerRef.current = await AppUpdate.addListener(
            'onFlexibleUpdateStateChange',
            (state) => {
              if (!mounted) return;
              if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
                addToast(
                  t('app_update.ready_to_install', 'Oppdatering lastet ned! Trykk for å installere.'),
                  'info',
                  { label: t('app_update.restart_button', 'Start på nytt'), onClick: completeUpdate },
                );
              } else if (state.installStatus === FlexibleUpdateInstallStatus.FAILED) {
                addToast(t('app_update.available', 'En oppdatering er tilgjengelig i Play Store'), 'info');
                AppUpdate.openAppStore().catch(() => {});
              }
            },
          );

          const result = await AppUpdate.startFlexibleUpdate();

          if (result.code !== AppUpdateResultCode.OK) {
            // User canceled or it failed — clean up listener
            listenerRef.current?.remove();
            listenerRef.current = null;
            // Only show fallback if it actually failed (not user cancel)
            if (result.code === AppUpdateResultCode.FAILED) {
              addToast(t('app_update.available', 'En oppdatering er tilgjengelig i Play Store'), 'info');
              AppUpdate.openAppStore().catch(() => {});
            }
          }
        } else {
          // Flexible not available — fall back to Play Store
          addToast(t('app_update.available', 'En oppdatering er tilgjengelig i Play Store'), 'info');
          AppUpdate.openAppStore().catch(() => {});
        }
      } catch {
        // Plugin error — not critical
      }
    };

    checkAndStartUpdate();

    // Re-check on resume (catches downloads that completed while backgrounded)
    const resumePromise = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkAndStartUpdate();
    });

    return () => {
      mounted = false;
      listenerRef.current?.remove();
      resumePromise.then((h) => h.remove()).catch(() => {});
    };
  }, [t, addToast, completeUpdate]);
}
